import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, useLang, useUnits, formatApiError, usePerm } from "../lib/providers";
import { PageHeader, Btn, Field, TextInput, TextArea, Select, Section } from "../components/primitives";
import SignaturePad from "../components/SignaturePad";
import { useConfirm } from "../components/ConfirmDialog";
import { Plus, Trash, ArrowLeft, FloppyDisk, FilePdf, Ruler, Receipt, XCircle, UploadSimple, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const emptyLine = { name: "", description: "", quantity: "", unit: "Stk" };

export default function AufmassEditorPage() {
  const { id } = useParams();
  const isNew = !id;
  const { t, lang } = useLang();
  const { units } = useUnits();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { can } = usePerm();
  const canEdit = can("aufmasse", "edit");
  const canCreate = can("aufmasse", "create");
  const canDelete = can("aufmasse", "delete");
  const canExport = can("aufmasse", "export");
  const canCreateInvoice = can("invoices", "create");

  const [customers, setCustomers] = useState([]);
  const [isStorno, setIsStorno] = useState(false);
  const [sourceAufmassNumber, setSourceAufmassNumber] = useState("");
  const [signedPdf, setSignedPdf] = useState({
    has: false,
    filename: "",
    uploaded_at: "",
  });
  const [uploadingSigned, setUploadingSigned] = useState(false);
  const signedInputRef = React.useRef(null);
  const [form, setForm] = useState({
    number: "",
    title: "",
    customer_id: "",
    measurement_date: new Date().toISOString().slice(0, 10),
    site_address: "",
    notes: "",
    customer_signature: "",
    customer_signature_name: "",
    employee_signature: "",
    employee_signature_name: "",
    purchase_order_number: "",
    lines: [],
    status: "draft",
  });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [customerMode, setCustomerMode] = useState("existing"); // "existing" | "free"
  const [freeCustomer, setFreeCustomer] = useState({ name: "", company: "", address: "", email: "", phone: "" });

  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => setCustomers([]));
    if (!isNew) {
      api.get(`/aufmasse/${id}`)
        .then((r) => {
          setForm((prev) => ({ ...prev, ...r.data }));
          setIsStorno(!!r.data.is_storno);
          setSourceAufmassNumber(r.data.source_aufmass_number || "");
          setSignedPdf({
            has: !!r.data.has_signed_pdf,
            filename: r.data.signed_pdf_filename || "",
            uploaded_at: r.data.signed_pdf_uploaded_at || "",
          });
          if (!r.data.customer_id && r.data.customer_snapshot) {
            setCustomerMode("free");
            setFreeCustomer({
              name: r.data.customer_snapshot.name || "",
              company: r.data.customer_snapshot.company || "",
              address: r.data.customer_snapshot.address || "",
              email: r.data.customer_snapshot.email || "",
              phone: r.data.customer_snapshot.phone || "",
            });
          }
        })
        .catch((err) => setError(formatApiError(err.response?.data?.detail) || err.message))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addLine = () => setForm({ ...form, lines: [...form.lines, { ...emptyLine }] });
  const removeLine = (i) => setForm({ ...form, lines: form.lines.filter((_, idx) => idx !== i) });
  const updateLine = (i, patch) => {
    const lines = form.lines.map((ln, idx) => (idx === i ? { ...ln, ...patch } : ln));
    setForm({ ...form, lines });
  };

  const isLocked = !isNew && (form.status === "signed" || form.status === "cancelled" || isStorno);

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...form,
        customer_id: customerMode === "existing" ? (form.customer_id || null) : null,
        customer_snapshot: customerMode === "free" ? freeCustomer : undefined,
        purchase_order_number: form.purchase_order_number || "",
        lines: form.lines.map((l) => ({
          name: l.name || "",
          description: l.description || "",
          quantity: l.quantity === "" || l.quantity === null ? null : Number(l.quantity),
          unit: l.unit || "",
        })),
      };
      if (isNew) {
        const { data } = await api.post("/aufmasse", payload);
        toast.success(lang === "de" ? "Aufmaß erstellt" : "Aufmaß created");
        navigate(`/aufmasse/${data.id}`, { replace: true });
      } else {
        const { data } = await api.put(`/aufmasse/${id}`, payload);
        setForm({ ...form, ...data });
        toast.success(lang === "de" ? "Gespeichert" : "Saved");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (isNew) return;
    const ok = await confirm({
      title: lang === "de" ? "Aufmaß löschen?" : "Delete Aufmaß?",
      description: lang === "de" ? "Diese Aktion kann nicht rückgängig gemacht werden." : "This cannot be undone.",
      confirmText: lang === "de" ? "Löschen" : "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/aufmasse/${id}`);
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
      navigate("/aufmasse", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const downloadPdf = async () => {
    if (isNew) {
      toast.info(lang === "de" ? "Bitte zuerst speichern." : "Please save first.");
      return;
    }
    try {
      const res = await api.get(`/aufmasse/${id}/pdf`, { responseType: "blob", params: { lang } });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${form.number || "aufmass"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const convertToInvoice = async () => {
    if (isNew) {
      toast.info(lang === "de" ? "Bitte zuerst speichern." : "Please save first.");
      return;
    }
    const ok = await confirm({
      title: lang === "de" ? "Rechnung aus Aufmaß erstellen?" : "Create invoice from Aufmaß?",
      description: lang === "de"
        ? "Es wird eine neue Rechnung mit den Mengen aus diesem Aufmaß erzeugt. Preise werden – sofern verfügbar – aus dem zugehörigen Angebot übernommen."
        : "A new invoice will be created using the quantities from this Aufmaß. Prices are taken from the linked quote where available.",
      confirmText: lang === "de" ? "Rechnung erstellen" : "Create invoice",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/aufmasse/${id}/convert-to-invoice`);
      toast.success(lang === "de" ? `Rechnung ${data.number} erstellt` : `Invoice ${data.number} created`);
      navigate(`/invoices/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const createStornoAufmass = async () => {
    if (isNew) return;
    const ok = await confirm({
      title: lang === "de" ? "Aufmaß stornieren?" : "Cancel Aufmaß?",
      description: lang === "de"
        ? "Es wird ein Storno-Aufmaß mit eigener fortlaufender Nummer erzeugt. Das Original wird eingefroren und ist anschließend nicht mehr bearbeitbar."
        : "A storno measurement with its own consecutive number will be created. The original will be locked.",
      confirmText: lang === "de" ? "Storno erstellen" : "Create storno",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/aufmasse/${id}/storno`);
      toast.success(lang === "de" ? `Storno ${data.number} erstellt` : `Storno ${data.number} created`);
      navigate(`/aufmasse/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === form.customer_id),
    [customers, form.customer_id],
  );

  const uploadSignedPdf = async (file) => {
    if (!file || isNew) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error(lang === "de" ? "Bitte eine PDF-Datei wählen" : "Please choose a PDF file");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error(lang === "de" ? "Datei zu groß (max. 12 MB)" : "File too large (max 12 MB)");
      return;
    }
    setUploadingSigned(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/aufmasse/${id}/signed-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSignedPdf({ has: true, filename: data.filename, uploaded_at: data.uploaded_at });
      toast.success(lang === "de" ? "Signiertes PDF hochgeladen" : "Signed PDF uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUploadingSigned(false);
      if (signedInputRef.current) signedInputRef.current.value = "";
    }
  };

  const viewSignedPdf = async () => {
    try {
      const res = await api.get(`/aufmasse/${id}/signed-pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => window.URL.revokeObjectURL(url), 30_000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteSignedPdf = async () => {
    const ok = await confirm({
      title: lang === "de" ? "Signiertes PDF entfernen?" : "Remove signed PDF?",
      description: lang === "de"
        ? "Der Anhang wird gelöscht. In PDF-Bundles wird künftig wieder das automatisch generierte Aufmaß verwendet."
        : "The attachment will be removed. Bundles will fall back to the auto-generated Aufmaß PDF.",
      confirmText: lang === "de" ? "Entfernen" : "Remove",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/aufmasse/${id}/signed-pdf`);
      setSignedPdf({ has: false, filename: "", uploaded_at: "" });
      toast.success(lang === "de" ? "Signiertes PDF entfernt" : "Signed PDF removed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="p-8 text-[var(--muted)]">…</div>;

  return (
    <div className="p-6 sm:p-8 max-w-[1100px] mx-auto">
      <PageHeader
        eyebrow={lang === "de" ? "02 / Angebote · Aufmaß" : "02 / Quotes · Aufmaß"}
        title={isNew ? (lang === "de" ? "Neues Aufmaß" : "New Aufmaß") : (form.number || "Aufmaß")}
        description={form.title}
        actions={
          <div className="flex flex-wrap gap-2">
            <Link to="/aufmasse">
              <Btn variant="ghost" data-testid="back-aufmass-list"><ArrowLeft size={16} /> {lang === "de" ? "Zurück" : "Back"}</Btn>
            </Link>
            {!isNew && canExport && (
              <Btn variant="secondary" onClick={downloadPdf} data-testid="aufmass-pdf"><FilePdf size={16} /> PDF</Btn>
            )}
            {!isNew && canCreateInvoice && !isStorno && form.status !== "cancelled" && (
              <Btn variant="secondary" onClick={convertToInvoice} data-testid="aufmass-to-invoice"><Receipt size={16} /> {lang === "de" ? "Rechnung erstellen" : "Create invoice"}</Btn>
            )}
            {!isNew && canCreate && isLocked && !isStorno && form.status !== "cancelled" && (
              <Btn variant="danger" onClick={createStornoAufmass} data-testid="aufmass-storno"><XCircle size={16} /> {lang === "de" ? "Storno" : "Storno"}</Btn>
            )}
            {!isNew && canDelete && !isLocked && (
              <Btn variant="secondary" onClick={del} data-testid="aufmass-delete" className="text-[#FF3B30]"><Trash size={16} /> {lang === "de" ? "Löschen" : "Delete"}</Btn>
            )}
            {(canEdit || canCreate) && (
              <Btn onClick={save} disabled={saving} data-testid="aufmass-save"><FloppyDisk size={16} /> {saving ? "…" : (lang === "de" ? "Speichern" : "Save")}</Btn>
            )}
          </div>
        }
      />

      {isLocked && (
        <div
          className="mb-4 text-sm text-[var(--muted)] border border-[var(--border)] bg-[var(--row-hover)] px-3 py-2 rounded-sm flex items-center gap-2"
          data-testid="aufmass-lock-banner"
        >
          <XCircle size={16} className="text-[var(--brand)]" />
          <span>{lang === "de"
            ? "Dieses Aufmaß ist eingefroren. Für Änderungen bitte ein Storno-Aufmaß erstellen."
            : "This Aufmaß is locked. To change it, please create a storno."}</span>
        </div>
      )}

      {error && <div className="mb-4 swiss-card p-3 text-sm text-[#FF3B30] border-l-4 border-l-[#FF3B30]" data-testid="aufmass-error">{error}</div>}

      {!isNew && (canEdit || canCreate) && (
        <div className="mb-6 swiss-card p-4" data-testid="signed-pdf-section">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-sm bg-[var(--brand)]/10 text-[var(--brand)] shrink-0">
              <FileText size={20} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="label-overline text-[var(--brand)]">{lang === "de" ? "Signiertes PDF" : "Signed PDF"}</div>
              {signedPdf.has ? (
                <div className="text-sm">
                  <span className="font-medium text-[var(--ink)]">{signedPdf.filename || "PDF"}</span>
                  {signedPdf.uploaded_at && (
                    <span className="ml-2 text-xs text-[var(--muted-2)]">
                      {lang === "de" ? "hochgeladen am " : "uploaded "}
                      {new Date(signedPdf.uploaded_at).toLocaleString(lang === "de" ? "de-DE" : "en-US")}
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-sm text-[var(--muted)]">
                  {lang === "de"
                    ? "Vom Kunden unterschriebenes Aufmaß als PDF hier hochladen — es wird in PDF-Sammlungen anstelle des automatisch erzeugten Aufmaßes verwendet."
                    : "Upload the customer-signed PDF here — it will replace the auto-generated PDF in bundles."}
                </div>
              )}
            </div>
            <input
              ref={signedInputRef}
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => uploadSignedPdf(e.target.files?.[0])}
              data-testid="signed-pdf-input"
            />
            {signedPdf.has && (
              <Btn variant="secondary" onClick={viewSignedPdf} data-testid="signed-pdf-view">
                <FilePdf size={16} /> {lang === "de" ? "Öffnen" : "View"}
              </Btn>
            )}
            <Btn
              variant="secondary"
              onClick={() => signedInputRef.current?.click()}
              disabled={uploadingSigned}
              data-testid="signed-pdf-upload"
            >
              <UploadSimple size={16} />
              {uploadingSigned
                ? (lang === "de" ? "Lädt hoch…" : "Uploading…")
                : (signedPdf.has ? (lang === "de" ? "Ersetzen" : "Replace") : (lang === "de" ? "Hochladen" : "Upload"))}
            </Btn>
            {signedPdf.has && (
              <Btn variant="ghost" onClick={deleteSignedPdf} className="text-[#FF3B30]" data-testid="signed-pdf-delete">
                <Trash size={16} />
              </Btn>
            )}
          </div>
        </div>
      )}

      <Section title={lang === "de" ? "Kopfdaten" : "Header"}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={lang === "de" ? "Titel / Bezeichnung" : "Title"} className="md:col-span-2">
            <TextInput data-testid="aufmass-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={lang === "de" ? "z. B. Aufmaß Sanierung Bad" : "e.g. Bathroom renovation"} />
          </Field>
          <Field label={t("customer")} className="md:col-span-2">
            <div className="flex gap-2 mb-2">
              <button type="button" onClick={() => setCustomerMode("existing")} data-testid="aufmass-customer-mode-existing"
                className={`px-3 h-8 text-xs rounded-sm border transition-colors ${customerMode === "existing" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"}`}>
                {lang === "de" ? "Vorhandener Kunde" : "Existing customer"}
              </button>
              <button type="button" onClick={() => setCustomerMode("free")} data-testid="aufmass-customer-mode-free"
                className={`px-3 h-8 text-xs rounded-sm border transition-colors ${customerMode === "free" ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-[var(--surface)] border-[var(--border)] text-[var(--muted)] hover:bg-[var(--row-hover)]"}`}>
                {lang === "de" ? "Frei eingeben" : "Free entry"}
              </button>
            </div>
            {customerMode === "existing" ? (
              <>
                <Select data-testid="aufmass-customer" value={form.customer_id || ""} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
                  <option value="">{lang === "de" ? "— wählen —" : "— select —"}</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>{c.company || c.name || "—"}{c.company && c.name ? ` · ${c.name}` : ""}</option>
                  ))}
                </Select>
                {selectedCustomer && (
                  <div className="mt-2 text-xs text-[var(--muted)] whitespace-pre-line">{selectedCustomer.address || ""}</div>
                )}
              </>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextInput data-testid="aufmass-free-name" placeholder={lang === "de" ? "Name" : "Name"} value={freeCustomer.name} onChange={(e) => setFreeCustomer({ ...freeCustomer, name: e.target.value })} />
                <TextInput data-testid="aufmass-free-company" placeholder={lang === "de" ? "Firma (optional)" : "Company (optional)"} value={freeCustomer.company} onChange={(e) => setFreeCustomer({ ...freeCustomer, company: e.target.value })} />
                <TextArea data-testid="aufmass-free-address" placeholder={lang === "de" ? "Adresse" : "Address"} value={freeCustomer.address} onChange={(e) => setFreeCustomer({ ...freeCustomer, address: e.target.value })} className="md:col-span-2" />
                <TextInput data-testid="aufmass-free-email" type="email" placeholder="E-Mail" value={freeCustomer.email} onChange={(e) => setFreeCustomer({ ...freeCustomer, email: e.target.value })} />
                <TextInput data-testid="aufmass-free-phone" placeholder={lang === "de" ? "Telefon" : "Phone"} value={freeCustomer.phone} onChange={(e) => setFreeCustomer({ ...freeCustomer, phone: e.target.value })} />
              </div>
            )}
          </Field>
          <Field label={lang === "de" ? "Aufmaßdatum" : "Measurement date"}>
            <TextInput type="date" data-testid="aufmass-date" value={form.measurement_date || ""} onChange={(e) => setForm({ ...form, measurement_date: e.target.value })} readOnly={isLocked} />
          </Field>
          <Field label={lang === "de" ? "Bestellnummer" : "PO number"}>
            <TextInput
              data-testid="aufmass-po-number"
              value={form.purchase_order_number || ""}
              onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
              placeholder={lang === "de" ? "z. B. BST-2026-001" : "e.g. PO-2026-001"}
            />
          </Field>
          <Field label={lang === "de" ? "Baustelle / Objekt-Adresse" : "Site address"} className="md:col-span-2">
            <TextArea data-testid="aufmass-site" value={form.site_address || ""} onChange={(e) => setForm({ ...form, site_address: e.target.value })} placeholder={lang === "de" ? "Straße, PLZ Ort" : "Street, ZIP city"} />
          </Field>
        </div>
      </Section>

      <div className="h-6" />

      <Section
        title={lang === "de" ? "Positionen" : "Lines"}
        right={(canEdit || canCreate) ? <Btn variant="secondary" onClick={addLine} data-testid="aufmass-add-line"><Plus size={14} /> {lang === "de" ? "Position" : "Line"}</Btn> : null}
      >
        {form.lines.length === 0 ? (
          <div className="text-sm text-[var(--muted-2)] py-6 text-center">
            {lang === "de" ? "Noch keine Positionen — füge die vor Ort erfassten Leistungen hinzu." : "No lines yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[var(--muted)] border-b border-[var(--border)]">
                <tr>
                  <th className="text-left py-2 pr-2 label-overline w-12">{lang === "de" ? "Pos." : "Pos."}</th>
                  <th className="text-left py-2 pr-2 label-overline">{lang === "de" ? "Beschreibung" : "Description"}</th>
                  <th className="text-right py-2 px-2 label-overline w-28">{lang === "de" ? "Menge" : "Qty"}</th>
                  <th className="text-left py-2 px-2 label-overline w-24">{lang === "de" ? "Einheit" : "Unit"}</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => (
                  <tr key={i} className="border-b border-[var(--border)]">
                    <td className="py-2 pr-2 text-[var(--muted)] font-mono">{i + 1}</td>
                    <td className="py-2 pr-2">
                      <TextInput data-testid={`aufmass-line-name-${i}`} value={l.name} onChange={(e) => updateLine(i, { name: e.target.value })} placeholder={lang === "de" ? "Bezeichnung" : "Name"} />
                      <TextArea data-testid={`aufmass-line-desc-${i}`} value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} placeholder={lang === "de" ? "Beschreibung (optional)" : "Description (optional)"} className="mt-2" />
                    </td>
                    <td className="py-2 px-2">
                      <TextInput
                        data-testid={`aufmass-line-qty-${i}`}
                        type="number"
                        step={(l.unit || "").toLowerCase() === "stk" ? "1" : "0.01"}
                        value={l.quantity}
                        onChange={(e) => {
                          const v = e.target.value;
                          const isStk = (l.unit || "").toLowerCase() === "stk";
                          updateLine(i, { quantity: isStk && v !== "" ? String(parseInt(v, 10) || 0) : v });
                        }}
                        className="text-right"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <Select data-testid={`aufmass-line-unit-${i}`} value={l.unit} onChange={(e) => updateLine(i, { unit: e.target.value })}>
                        {(units || []).map((u) => <option key={u.value} value={u.value}>{u.value}</option>)}
                      </Select>
                    </td>
                    <td className="py-2 text-right">
                      {(canEdit || canCreate) && (
                        <button type="button" onClick={() => removeLine(i)} className="text-[var(--muted-2)] hover:text-[#FF3B30]" data-testid={`aufmass-line-del-${i}`}>
                          <Trash size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <div className="h-6" />

      <Section title={lang === "de" ? "Anmerkungen" : "Notes"}>
        <TextArea data-testid="aufmass-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} placeholder={lang === "de" ? "Besondere Hinweise, Mängel, Vereinbarungen …" : "Special notes, issues, agreements …"} />
      </Section>

      <div className="h-6" />

      <Section
        title={lang === "de" ? "Unterschriften" : "Signatures"}
        right={
          <span className="text-xs text-[var(--muted-2)]">
            <Ruler size={12} className="inline mr-1" />
            {lang === "de" ? "Direkt auf Tablet/Touchscreen unterschreiben" : "Sign directly on tablet"}
          </span>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <div className="label-overline mb-2">{lang === "de" ? "Kunde" : "Customer"}</div>
            <SignaturePad
              testId="aufmass-cust-sig"
              value={form.customer_signature}
              onChange={(url) => setForm({ ...form, customer_signature: url })}
              label={lang === "de" ? "Löschen" : "Clear"}
            />
            <div className="mt-3">
              <Field label={lang === "de" ? "Name in Druckbuchstaben" : "Printed name"}>
                <TextInput data-testid="aufmass-cust-name" value={form.customer_signature_name} onChange={(e) => setForm({ ...form, customer_signature_name: e.target.value })} placeholder={lang === "de" ? "Max Mustermann" : "John Doe"} />
              </Field>
            </div>
          </div>
          <div>
            <div className="label-overline mb-2">{lang === "de" ? "Mitarbeiter (optional)" : "Employee (optional)"}</div>
            <SignaturePad
              testId="aufmass-emp-sig"
              value={form.employee_signature}
              onChange={(url) => setForm({ ...form, employee_signature: url })}
              label={lang === "de" ? "Löschen" : "Clear"}
            />
            <div className="mt-3">
              <Field label={lang === "de" ? "Name in Druckbuchstaben" : "Printed name"}>
                <TextInput data-testid="aufmass-emp-name" value={form.employee_signature_name} onChange={(e) => setForm({ ...form, employee_signature_name: e.target.value })} />
              </Field>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
